import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import { useState } from "react";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import CurriculumTableData from "Views/Curriculum/data/curriculumTableData";
import ArgonButton from "components/ArgonButton";
import axios from "utils/api";
import { baseURL } from "utils/api";
import { toast } from "react-toastify";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import { Close } from "@mui/icons-material";

function Tables() {
  const [refresh, setRefresh] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Add/Edit form dialog state
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add"); // 'add' | 'edit'
  const [editId, setEditId] = useState(null);
  const [className, setClassName] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  // Delete button click handler
  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setDeleteId(null); // reset selected id
  };

  // Open Add form
  const handleAddClick = () => {
    setFormMode("add");
    setClassName("");
    setEditId(null);
    setFormOpen(true);
  };

  // Open Edit form from table
  const handleEditClick = (id, name) => {
    setFormMode("edit");
    setEditId(id);
    setClassName(name || "");
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setFormLoading(false);
    setClassName("");
    setEditId(null);
  };

  // Submit Add/Edit
  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!className.trim()) {
      toast.error("Class name cannot be empty");
      return;
    }
    setFormLoading(true);
    if (formMode === "add") {
      axios
        .post(`${baseURL}/classes`, { name: className })
        .then((res) => {
          toast.success(res.data?.message || "Class added successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || "Failed to add class");
        })
        .finally(() => setFormLoading(false));
    } else {
      axios
        .put(`${baseURL}/classes/${editId}`, { name: className })
        .then((res) => {
          toast.success(res.data?.message || "Class updated successfully");
          setRefresh((prev) => !prev);
          handleCloseForm();
        })
        .catch((err) => {
          toast.error(err.response?.data?.error || "Failed to update class");
        })
        .finally(() => setFormLoading(false));
    }
  };

  const handleConfirmDelete = () => {
    axios
      .put(`${baseURL}/classes/${deleteId}`, { isDeleted: true })
      .then((res) => {
        toast.success(res.data?.message || "Class deleted successfully");
        setRefresh((prev) => !prev);
        handleCloseDialog();
      })
      .catch((err) => {
        toast.error(err.response?.data?.error || "Failed to delete class");
      });
  };

  const { columns, rows, count } = CurriculumTableData(handleDeleteClick, handleEditClick, refresh);

  return (
    <ArgonBox>
      <ArgonBox display="flex" justifyContent="space-between" alignItems="center" px={4} mb={3}>
        <ArgonTypography variant="h6" size="small">
          All Class
        </ArgonTypography>
        <ArgonButton variant="contained" color="primary" size="small" onClick={handleAddClick}>
          Add Class
        </ArgonButton>
      </ArgonBox>
      <ArgonBox
        sx={{
          "& .MuiTableRow-root:not(:last-child)": {
            "& td": {
              borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                `${borderWidth[1]} solid ${borderColor}`,
            },
          },
        }}
      >
        {rows.length > 0 ? (
          <Table columns={columns} rows={rows} />
        ) : (
          <ArgonBox textAlign="center" py={4}>
            <ArgonTypography variant="h6" color="text" fontWeight="medium">
              No class found
            </ArgonTypography>
            <ArgonTypography variant="caption" color="secondary">
              Click &apos;Add Class&apos; to create a new class.
            </ArgonTypography>
          </ArgonBox>
        )}
      </ArgonBox>

      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Class"
        description="Are you sure you want to delete this class?"
        confirmText="Delete Class"
        cancelText="Cancel"
      />

      {/* Add/Edit Dialog */}
      <Dialog open={formOpen} onClose={handleCloseForm} maxWidth="sm" fullWidth>
        <DialogTitle>
          <ArgonBox display="flex" justifyContent="space-between" alignItems="center">
            <ArgonTypography variant="h5">
              {formMode === "add" ? "Add Class" : "Edit Class"}
            </ArgonTypography>
            <IconButton onClick={handleCloseForm} size="small">
              <Close />
            </IconButton>
          </ArgonBox>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit}>
          <DialogContent>
            <ArgonBox mb={2}>
              <ArgonTypography variant="caption" fontWeight="medium" mb={1}>
                Class Name
              </ArgonTypography>
              <ArgonBox
                component="input"
                type="text"
                value={className}
                onChange={(e) => setClassName(e.target.value)}
                placeholder="Enter Class Name"
                disabled={formLoading}
                sx={{
                  width: "100%",
                  padding: "10px 12px",
                  fontSize: "14px",
                  border: "1px solid #d2d6da",
                  borderRadius: "8px",
                  outline: "none",
                }}
              />
            </ArgonBox>
          </DialogContent>
          <DialogActions>
            <ArgonButton
              onClick={handleCloseForm}
              variant="outlined"
              color="secondary"
              disabled={formLoading}
            >
              Cancel
            </ArgonButton>
            <ArgonButton type="submit" variant="contained" color="primary" disabled={formLoading}>
              {formMode === "add"
                ? formLoading
                  ? "Adding..."
                  : "Add Class"
                : formLoading
                ? "Updating..."
                : "Update Class"}
            </ArgonButton>
          </DialogActions>
        </form>
      </Dialog>
    </ArgonBox>
  );
}

export default Tables;
